/* ==========================================================================
   FK CRO — Flow State build, Aug 2026
   1. Free-shipping progress bars stay in sync with the cart
   2. Mobile sticky add-to-cart mirrors the real product form
   3. Quick-add size buttons on collection cards
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.FKCRO || {};
  var THRESHOLD = Number(CFG.freeShippingThreshold || 0); // in cents
  var money = CFG.moneyFormat || '${{amount}}';

  /* ---------------------------------------------------------------- utils */

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return money.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  function fetchCart() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  /* ------------------------------------------------- 1. shipping progress */

  function paintShipping(total) {
    var bars = document.querySelectorAll('[data-fk-ship]');
    if (!bars.length || !THRESHOLD) return;

    var remaining = Math.max(THRESHOLD - total, 0);
    var pct = THRESHOLD > 0 ? Math.min((total / THRESHOLD) * 100, 100) : 0;

    Array.prototype.forEach.call(bars, function (bar) {
      var fill = bar.querySelector('[data-fk-ship-fill]');
      var label = bar.querySelector('[data-fk-ship-label]');

      if (fill) fill.style.width = pct + '%';

      if (label) {
        if (remaining <= 0) {
          label.innerHTML = bar.getAttribute('data-fk-ship-met') || "You've unlocked free shipping";
          bar.classList.add('fk-ship--met');
        } else {
          var tpl = bar.getAttribute('data-fk-ship-away') || "You're <em>[amount]</em> away from free shipping";
          label.innerHTML = tpl.replace('[amount]', formatMoney(remaining));
          bar.classList.remove('fk-ship--met');
        }
      }
    });
  }

  function refreshShipping() {
    fetchCart().then(function (cart) {
      if (cart) paintShipping(cart.total_price);
    });
  }

  // Paint from the server-rendered value immediately so there is no flash of
  // a wrong number, then reconcile against the live cart. The server value can
  // be stale on a cached page, so the fetch is not optional.
  if (typeof CFG.cartTotal === 'number') paintShipping(CFG.cartTotal);
  refreshShippingOnIdle();

  function refreshShippingOnIdle() {
    var run = function () {
      refreshShipping();
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 300);
    }
  }

  // Dawn publishes cart updates through its pubsub helper; fall back to
  // watching fetch() for cart mutations when that isn't available.
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refreshShipping);
  }

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : url && url.url ? url.url : '';
    var result = nativeFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(href)) {
      result.then(function () {
        setTimeout(refreshShipping, 60);
      });
    }
    return result;
  };

  /* ------------------------------------------------- 2. sticky add-to-cart */

  function initSticky() {
    var bar = document.querySelector('[data-fk-sticky]');
    if (!bar) return;

    var form = document.querySelector('product-info form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]');
    var anchor = document.querySelector('product-info .product-form__buttons')
      || document.querySelector('.product-form__buttons')
      || form;

    var btn = bar.querySelector('[data-fk-sticky-btn]');
    var sizeOut = bar.querySelector('[data-fk-sticky-size]');

    if (!form || !anchor || !btn) return;

    /* Show the bar only once the real buy button has scrolled out of view. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { rootMargin: '0px 0px -40px 0px', threshold: 0 }
      );
      io.observe(anchor);
    }

    /* Mirror the currently selected variant into the bar. */
    function syncSelection() {
      var checked = form.querySelector('input[type="radio"]:checked, select option:checked');
      var label = '';
      if (checked) {
        label = (checked.value || checked.textContent || '').trim();
      }
      if (sizeOut) sizeOut.textContent = label ? ' · Size ' + label : '';

      var realBtn = form.querySelector('[name="add"]');
      if (realBtn) {
        btn.disabled = realBtn.disabled;
        var realText = realBtn.querySelector('span');
        if (realBtn.disabled) {
          btn.textContent = (realText ? realText.textContent : 'Sold out').trim();
        } else {
          btn.textContent = 'Add to bag';
        }
      }
    }

    form.addEventListener('change', syncSelection);
    document.addEventListener('variant:change', syncSelection);
    syncSelection();

    /* Submitting the real form keeps every existing cart behaviour intact. */
    btn.addEventListener('click', function () {
      var realBtn = form.querySelector('[name="add"]');
      if (realBtn && !realBtn.disabled) {
        realBtn.click();
      }
    });
  }

  /* ------------------------------------------------ 3. card size quick add */

  function initCardSizes() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-fk-card-add]');
      if (!btn || btn.disabled) return;

      event.preventDefault();
      var list = btn.closest('.fk-card-sizes');
      var id = btn.getAttribute('data-fk-card-add');
      if (!id) return;

      if (list) list.classList.add('is-busy');

      fetch(
        (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        }
      )
        .then(function (r) {
          return r.json();
        })
        .then(function () {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            if (typeof drawer.getSectionsToRender === 'function') {
              // Let Dawn re-render its own sections before opening.
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }
            drawer.open();
          }
          refreshShipping();
        })
        .catch(function () {
          /* Leave the page untouched on failure — the size row simply re-enables. */
        })
        .finally(function () {
          if (list) list.classList.remove('is-busy');
        });
    });
  }

  /* ------------------------------------------------- 4. size help shortcuts */

  function findAccordion(words) {
    var nodes = document.querySelectorAll('summary, .accordion__title, h2, h3, button');
    for (var i = 0; i < nodes.length; i++) {
      var txt = (nodes[i].textContent || '').trim().toLowerCase();
      for (var w = 0; w < words.length; w++) {
        if (txt.indexOf(words[w]) !== -1) return nodes[i];
      }
    }
    return null;
  }

  function openAndScroll(el) {
    if (!el) return false;
    var details = el.closest ? el.closest('details') : null;
    if (details) details.open = true;
    else if (el.click) el.click();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }

  function initSizeHelp() {
    // Both controls aim at the same place: whatever the theme already uses to
    // explain sizing. Only fall through to the size guide page if nothing on
    // the page can answer it.
    document.addEventListener('click', function (event) {
      var find = event.target.closest('[data-fk-sizehelp]');
      var chart = event.target.closest('[data-fk-sizechart]');
      if (!find && !chart) return;
      var target = findAccordion(['size chart', 'size guide', 'sizing']);
      if (target) {
        event.preventDefault();
        openAndScroll(target);
      }
      // No match: the anchor's href takes the visitor to the size guide page.
    });
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    initSticky();
    initCardSizes();
    initSizeHelp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
